import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Search, MessageCircle, Copy, RefreshCw, ChevronDown } from "lucide-react";

type ContactLog = {
  id: string; created_at: string; name: string | null; email: string | null; phone: string | null;
  company: string | null; service: string | null; message: string | null; email_sent: boolean | null;
  rd_status: number | null; rd_response: string | null; error_message: string | null;
};

type Origin = "all" | "site" | "blog";
const ORIGINS: { key: Origin; label: string }[] = [
  { key: "all", label: "Todos" }, { key: "site", label: "Formulário do site" }, { key: "blog", label: "Blog" },
];

const originOf = (l: ContactLog): Exclude<Origin, "all"> => ((l.service ?? "").toLowerCase().includes("blog") ? "blog" : "site");

/** Brazilian number -> wa.me digits (adds 55 when missing). */
export const waLink = (phone: string) => {
  const d = phone.replace(/\D/g, "");
  if (!d) return null;
  return `https://wa.me/${d.length <= 11 ? `55${d}` : d}`;
};

const ContactsManager = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState<ContactLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [origin, setOrigin] = useState<Origin>("all");
  const [open, setOpen] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("contact_logs").select("*").order("created_at", { ascending: false }).limit(500);
    if (error) toast({ title: "Erro ao carregar contatos", description: error.message, variant: "destructive" });
    setLogs((data as ContactLog[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qd = q.replace(/\D/g, "");
    return logs.filter((l) => {
      if (origin !== "all" && originOf(l) !== origin) return false;
      if (!q) return true;
      return [l.name, l.email, l.company].some((v) => (v ?? "").toLowerCase().includes(q)) ||
        (!!qd && (l.phone ?? "").replace(/\D/g, "").includes(qd));
    });
  }, [logs, search, origin]);

  const copy = async (v: string) => { await navigator.clipboard.writeText(v); toast({ title: "E-mail copiado" }); };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, e-mail, telefone ou empresa" className="pl-9" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Atualizar
        </Button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {ORIGINS.map((o) => {
          const n = o.key === "all" ? logs.length : logs.filter((l) => originOf(l) === o.key).length;
          return (
            <button key={o.key} onClick={() => setOrigin(o.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${origin === o.key ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"}`}>
              {o.label} · {n}
            </button>
          );
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <p className="text-muted-foreground">{logs.length === 0 ? "Nenhum contato recebido ainda." : "Nenhum contato encontrado com esses filtros."}</p>
      )}

      <div className="space-y-3">
        {filtered.map((l) => {
          const wa = l.phone ? waLink(l.phone) : null;
          const ok = l.rd_status && l.rd_status >= 200 && l.rd_status < 300;
          const isOpen = open === l.id;
          return (
            <Card key={l.id} className="transition-colors hover:border-primary/40">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center font-heading font-semibold">
                    {(l.name ?? "?").trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{l.name || "Sem nome"}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${originOf(l) === "blog" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {originOf(l) === "blog" ? "Blog" : "Formulário do site"}
                      </span>
                      {l.company && <span className="text-xs text-muted-foreground">{l.company}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(l.created_at).toLocaleString("pt-BR")}{l.service ? ` · ${l.service}` : ""}
                    </p>
                    {l.message && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.message}</p>}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {wa && (
                      <Button size="sm" asChild className="gap-1">
                        <a href={wa} target="_blank" rel="noopener noreferrer"><MessageCircle size={14} /> WhatsApp</a>
                      </Button>
                    )}
                    {l.email && (
                      <Button size="icon" variant="ghost" onClick={() => copy(l.email!)} title="Copiar e-mail" aria-label="Copiar e-mail"><Copy size={15} /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setOpen(isOpen ? null : l.id)} aria-label="Ver detalhes">
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="pl-[52px] space-y-2 text-sm">
                    <div className="grid md:grid-cols-2 gap-1">
                      <p><strong>E-mail:</strong> {l.email || "—"}</p>
                      <p><strong>Telefone:</strong> {l.phone || "—"}</p>
                    </div>
                    {l.message && <p className="whitespace-pre-wrap"><strong>Mensagem:</strong> {l.message}</p>}
                    <div className="flex gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${l.email_sent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>E-mail: {l.email_sent ? "enviado" : "não enviado"}</span>
                      <span className={`px-2 py-0.5 rounded ${ok ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>CRM: {l.rd_status ?? "sem resposta"}</span>
                    </div>
                    {l.rd_response && (
                      <details className="text-xs"><summary className="cursor-pointer">Resposta do CRM</summary>
                        <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto whitespace-pre-wrap">{l.rd_response}</pre></details>
                    )}
                    {l.error_message && <p className="text-destructive text-xs"><strong>Erro:</strong> {l.error_message}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ContactsManager;
